// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import { Alert } from '@loa/ui-components';
import type { ProfileFormVersion, RiskFlag, SustainabilityResult } from '../../api/profileFormApi';

/**
 * Advisor read-only view of a client's Sustainability Assessment (SUSTAINABILITY)
 * result: the preference-profile rows + the Signals & flags (advisor-facing).
 * Sustainability is computed-but-not-scored, so there is no 1–7 scale. View-only
 * (no Re-take). Styled with design tokens + the shared `Alert`.
 */

const LILAC_PANEL =
  'self-stretch rounded-card bg-lilac-06 flex flex-col items-center gap-xl p-lg lg:py-xxl';

const VARIANT: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
  ok: 'success',
  info: 'info',
  warn: 'warning',
  alert: 'error',
};

function variantFor(severity: string): 'info' | 'success' | 'warning' | 'error' {
  return VARIANT[severity?.toLowerCase()] ?? 'info';
}

/**
 * Render a preference-profile row value. List-style values (PAI areas / exclusions,
 * comma-joined by the profiler) are stacked one item per line; an "X — a, b, c"
 * value keeps its lead ("Considered —") on its own line. Plain values render as-is.
 */
function renderValue(value: string): React.ReactNode {
  const dashIdx = value.indexOf(' — ');
  const hasLeadList = dashIdx >= 0 && value.slice(dashIdx).includes(',');

  if (hasLeadList) {
    const lead = value.slice(0, dashIdx + 2);
    const items = value.slice(dashIdx + 3).split(', ').filter(Boolean);
    return (
      <span className="flex flex-col gap-xs">
        <span>{lead}</span>
        {items.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </span>
    );
  }

  if (value.includes(', ')) {
    const items = value.split(', ').filter(Boolean);
    return (
      <span className="flex flex-col gap-xs">
        {items.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </span>
    );
  }

  return value;
}

export function SustainabilityReadout({ latest }: { latest: ProfileFormVersion }) {
  const result = (latest.result ?? { profile: [], flags: [] }) as SustainabilityResult;
  const rows = result.profile ?? [];
  const flags: RiskFlag[] = result.flags ?? [];

  return (
    <div className={LILAC_PANEL}>
      <div
        data-testid="sustainability-assessment-readout"
        className="flex flex-col items-center gap-md self-stretch w-full"
      >
        <p className="text-black text-center font-sans text-body-04 font-medium leading-normal m-0">
          Sustainability Preference Profile
        </p>

        {rows.length > 0 && (
          <div className="w-full rounded-card border border-grey-05 bg-white overflow-hidden">
            {rows.map((r, i) => (
              <div
                key={r.label}
                data-testid={`sustainability-profile-row-${i}`}
                className={`flex gap-md px-md py-sm ${i < rows.length - 1 ? 'border-b border-grey-05' : ''}`}
              >
                <span className="basis-[44%] shrink-0 font-sans text-body-03 text-grey-02">{r.label}</span>
                <span className="flex-1 font-sans text-body-02 font-medium text-black">{renderValue(r.value)}</span>
              </div>
            ))}
          </div>
        )}

        {flags.length > 0 && (
          <div className="w-full flex flex-col gap-sm">
            <div className="font-sans text-body-03 font-medium text-grey-02">Signals &amp; flags</div>
            {flags.map((f) => (
              <div key={f.key} data-testid={`sustainability-flag-${f.key}`}>
                <Alert variant={variantFor(f.severity)}>{f.message}</Alert>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SustainabilityReadout;
